/**
 * Trainova AI — Video & Meeting Recording Types (Phase 11+)
 */

export type RecordingState =
  | 'IDLE'
  | 'INITIALIZING'
  | 'STARTING'
  | 'READY'
  | 'RECORDING'
  | 'STOPPING'
  | 'UPLOADING'
  | 'UPLOADED'
  | 'PROCESSING'
  | 'READY_TO_PLAY'
  | 'FAILED'
  | 'CANCELLED'
  | 'DISABLED';

export type CaptureMode = 'AUDIO_ONLY' | 'VIDEO' | 'AUDIO_VIDEO';

export interface RecordingHealth {
  isMicLive: boolean;
  isCameraLive: boolean;
  isRecording: boolean;
  healthWarning: string | null;
}

export interface VideoRecordingDetails {
  recordingId: string;
  attemptId: string;
  scenarioId?: string;
  status: RecordingState;
  captureMode?: CaptureMode;
  storageProvider: 'cloudinary' | 's3' | 'local';
  storageKey?: string;
  downloadUrl?: string;
  publicUrl?: string;
  playbackUrl?: string;
  mimeType?: string;
  fileSize?: number;
  durationSeconds?: number;
  startedAt?: string;
  endedAt?: string;
  uploadedAt?: string;
  errorReason?: string;
  metadata?: Record<string, unknown>;
}

export interface VideoAnalysisConfig {
  videoAnalysisEnabled: boolean;
  samplingIntervalMs: number;
  detectorType: 'tinyFaceDetector';
  inputSize: number;
  scoreThreshold: number;
  maxFaces: number;
  enableLandmarks: boolean;
  enableHeadPose: boolean;
  enableExpressionAnalysis: boolean;
  minimumFaceWidthRatio: number;
  noFaceWarningSeconds: number;
  targetFaceVisiblePercentage: number;
  targetFaceCenteredPercentage: number;
  maxYawDegrees: number;
  maxPitchDegrees: number;
  maxRollDegrees: number;
  inferenceTimeoutMs: number;
  storeRawFrames: boolean;
}

export const DEFAULT_VIDEO_ANALYSIS_CONFIG: VideoAnalysisConfig = Object.freeze({
  videoAnalysisEnabled: false,
  samplingIntervalMs: 2000,
  detectorType: 'tinyFaceDetector',
  inputSize: 320,
  scoreThreshold: 0.55,
  maxFaces: 3,
  enableLandmarks: true,
  enableHeadPose: false,
  enableExpressionAnalysis: false,
  minimumFaceWidthRatio: 0.1,
  noFaceWarningSeconds: 10,
  targetFaceVisiblePercentage: 80,
  targetFaceCenteredPercentage: 70,
  maxYawDegrees: 25,
  maxPitchDegrees: 20,
  maxRollDegrees: 20,
  inferenceTimeoutMs: 5000,
  storeRawFrames: false,
});

export interface HeadPoseSample {
  yaw: number;
  pitch: number;
  roll: number;
}

export interface HeadPoseSummary {
  supported: boolean;
  samplesEvaluated: number;
  withinThresholdPercentage: number;
  averageYaw: number;
  averagePitch: number;
  averageRoll: number;
  maxYaw: number;
  maxPitch: number;
  maxRoll: number;
  exceededCount: number;
  note: string;
}

export interface FacePositionMetrics {
  centerX: number;
  centerY: number;
  widthRatio: number;
  heightRatio: number;
  isCentered: boolean;
  isTooSmall: boolean;
  isPartiallyOutside: boolean;
}

export interface FaceAnalysisSummary {
  status: 'PENDING' | 'INITIALIZING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'DISABLED' | 'UNAVAILABLE';
  modelVersion: string;
  effectiveConfig: VideoAnalysisConfig;
  samplingIntervalMs: number;
  totalDurationMs: number;
  totalSamples: number;
  validSampledFrames: number;
  failedSamplesCount: number;
  faceDetectedSamples: number;
  faceVisiblePercentage: number | null;
  faceCenteredPercentage: number | null;
  averageConfidence: number | null;
  minConfidence: number | null;
  maxConfidence: number | null;
  maxFacesDetectedInSingleFrame: number;
  multipleFacesEventsCount: number;
  missingPeriodsCount: number;
  missingPeriods: Array<{
    startOffsetMs: number;
    endOffsetMs: number;
    durationMs: number;
    warningIssued?: boolean;
  }>;
  headPoseSummary?: HeadPoseSummary;
  expressionsSummary?: {
    neutral: number;
    happy: number;
    sad: number;
    angry: number;
    fearful: number;
    disgusted: number;
    surprised: number;
  };
  warnings: string[];
  failureReason?: string;
}

export interface UseCameraRecorderOptions {
  attemptId?: string;
  scenarioId?: string;
  captureMode?: CaptureMode;
  autoStart?: boolean;
  cameraStream?: MediaStream | null;
  audioStream?: MediaStream | null;
  stream?: MediaStream | null;
  onRecordingComplete?: (details: Partial<VideoRecordingDetails>) => void;
  onError?: (error: string) => void;
}
